// src/routes/_regular/join-us/index.jsx
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { PricingTable } from '@/components/PricingTable'
import { Button } from '@/components/ui/button'
import { Store, Package, BarChart3, Sparkles, Users, MapPin } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import HeroSection from '@/components/marketing/HeroSection'
import FeaturesGrid from '@/components/marketing/FeaturesGrid'
import LocalPresenceSection from '@/components/marketing/LocalPresenceSection'
import CTABanner from '@/components/marketing/CTABanner'

const heroImg = '/3.png'
const featuresImg = '/4.png'
const ctaImg = '/8.png'

export const Route = createFileRoute('/_regular/join-us/')({
  component: JoinUsPage,
  head: () => ({
    meta: [
      { title: 'أنشئ متجرك | سكيكيمة' },
      {
        name: 'description',
        content: 'انضم إلى سكيكيمة وأنشئ متجرك للوصول إلى آلاف العملاء في الصحراء.',
      },
    ],
  }),
})

function JoinUsPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const handleCreateStore = () => {
    navigate({ to: '/dashboard/create-store' })
  }

  return (
    <div className="min-h-screen bg-background" dir="rtl">
        <HeroSection
        title={
            <>
            أطلق متجرك الإلكتروني مع{" "}
            <span className="text-primary">سكيكيمة</span>
            </>
        }
        subtitle="انضم إلى أكبر سوق محلي في المنطقة – واجهة بيع احترافية، وسائل دفع متكاملة، ووصول إلى آلاف العملاء."
        buttonText="ابدأ متجرك الآن"
        onButtonClick={handleCreateStore}
        imageSrc={heroImg}
        imageAlt="سوق سكيكيمة"
        />

      <FeaturesGrid
        title="لماذا سكيكيمة؟"
        subtitle="منصة متكاملة تمكنك من إدارة متجرك بكل سهولة والوصول إلى زبائنك أينما كانوا."
        features={[
          { icon: Store, title: 'متجر احترافي', desc: 'تصميم عصري يتكيف مع جميع الأجهزة' },
          { icon: Package, title: 'إدارة المنتجات', desc: 'رفع منتجات غير محدودة، تصنيفات، مراجعات' },
          { icon: BarChart3, title: 'إحصائيات متقدمة', desc: 'تعرف على أداء متجرك والمبيعات' },
          { icon: Sparkles, title: 'ترويج بالإعلانات', desc: 'زد ظهور منتجاتك عبر الإعلانات المدفوعة' },
          { icon: Users, title: 'دعم العملاء', desc: 'فريق متخصص يساعدك على مدار الساعة' },
          { icon: MapPin, title: 'أسواق محلية', desc: 'استهدف زبائن في مدينتك بسهولة' },
        ]}
        cardClassName="p-4"
        iconClassName="h-11 w-11"
        titleClassName="text-lg"
        descClassName="text-sm"
      />

      <LocalPresenceSection
        title="نحن حيث أنت"
        subtitle="سكيكيمة متواجدة في أكبر المدن والأحياء بالصحراء. انضم إلى آلاف التجار الذين يثقون بنا ويوسعون نطاق أعمالهم يومياً."
        cities={['العيون', 'السمارة', 'بوجدور', 'الطرفاية', 'المرسى']}
        neighborhoods={['شارع سكيكيمة', 'حي الولاية', 'كولومينا', 'الحشيشة', 'النهضة', 'شارع بوكراع', 'حي الانعاش', 'المسيرة']}
        imageSrc={featuresImg}
        badgeText="حضور محلي قوي"
        showTrustSignals={true}
      />

      {/* Pricing Section – unique to this page */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">خطط تناسب كل المشاريع</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              اختر الخطة المناسبة لمتجرك – ابدأ مجاناً ثم ارتقِ مع نمو أعمالك.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1 text-sm text-primary">
              ✨ شهرين مجاناً على الباقة الاحترافية للمتاجر الجديدة
            </div>
          </div>
          <PricingTable onSelectPlan={handleCreateStore} loading={false} />
          <p className="text-center text-sm text-muted-foreground mt-8">
            * الفترة التجريبية المجانية للباقة الاحترافية مدتها شهران. بعدها اختر ما يناسبك.
          </p>
        </div>
      </section>

      <CTABanner
        title="جاهز لبدء مشروعك؟"
        description="انضم إلى آلاف المتاجر التي بدأت رحلتها مع سكيكيمة اليوم."
        buttonText="إنشاء متجر جديد"
        onButtonClick={handleCreateStore}
        imageSrc={ctaImg}
      />
    </div>
  )
}